import DOMPurify from 'dompurify'
import { ChainTypes } from '../models/chainTypes'

export const setNestedValue = (base, fieldPath, value) => {
  if (!base || typeof base !== 'object') {
    return setNestedValue({}, fieldPath, value)
  }
  if (fieldPath.indexOf('.') === -1) {
    return { ...base, [fieldPath]: value }
  }
  const fields = fieldPath.split('.')
  return {
    ...base,
    [fields[0]]: setNestedValue(
      base[fields[0]],
      fields.splice(1).join('.'),
      value,
    ),
  }
}

export const getDeep = (base, field, ...fields) => {
  if (field == null) {
    return base
  }
  if (!base || typeof base !== 'object') {
    return null
  }
  if (typeof field === 'string' && field.indexOf('.') !== -1) {
    return getDeep(base[field.split('.')[0]], ...field.split('.').splice(1))
  }
  return getDeep(base[field], fields[0], ...fields.splice(1))
}

export const getDeepOr = (defaultValue, base, field, ...fields) => {
  const deep = getDeep(base, field, ...fields)
  return deep != null ? deep : defaultValue
}

export const actOnData = (data, func) => (data != null ? func(data) : null)

export const recentlyCreated = created => {
  const now = new Date()
  const msBetweenDates = Math.abs(now.getTime() - new Date(created).getTime())
  const daysBetweenDates = msBetweenDates / (24 * 60 * 60 * 1000)
  return daysBetweenDates <= 90
}

export const isUserInRole = ({ role: userRole } = { role: '' }, role = '') =>
  userRole === role

export const userHasAuthority = (
  { authorities } = { authorities: [] },
  authority = '',
) => authorities.includes(authority)

const stringOperation = func => arg => {
  if (typeof arg === 'string') {
    return func(arg)
  }
  if (Array.isArray(arg)) {
    return arg.map(func)
  }
  if (typeof arg === 'object') {
    const temp = {}
    Object.keys(arg).forEach(k => {
      temp[k] = func(arg[k])
    })
    return temp
  }
  return `${arg}`
}

export const capitalizeFirstLetter = stringOperation(
  arg => arg.charAt(0).toUpperCase() + arg.substring(1),
)

export const nonBreakingSpace = stringOperation(arg =>
  arg.replace(/ /g, '\u00A0'),
)

export const round = (value, decimals) =>
  Number(`${Math.round(`${value}e${decimals}`)}e-${decimals}`)

export const requestPoller = (
  asyncRequest,
  fulfilledPredicate,
  timeoutMillis,
  maxTimeMillis,
  intervalHandler = () => {},
) =>
  new Promise((resolve, reject) => {
    let intervalRef = -1
    const startTime = Date.now()
    const poller = () => {
      if (Date.now() - startTime < maxTimeMillis) {
        asyncRequest()
          .then(resp => {
            if (fulfilledPredicate(resp)) {
              clearInterval(intervalRef)
              intervalHandler(null)
              resolve(resp)
            }
          })
          .catch(e => {
            clearInterval(intervalRef)
            intervalHandler(null)
            reject(e)
          })
      } else {
        clearInterval(intervalRef)
        intervalHandler(null)
        reject()
      }
    }
    intervalRef = setInterval(poller, timeoutMillis)
    intervalHandler(intervalRef)
  })

export const isFranchiseeOrNoRelations = ({
  storeInfo: {
    currentRepresentative: { chainType },
  },
}) => [ChainTypes.NO_RELATIONS, ChainTypes.FRANCHISEE].includes(chainType)

export const getRepresentativeId = ({
  auth: {
    representative: { representativeUID },
  },
}) => representativeUID

export const getStoreId = ({
  auth: {
    store: { storeUID },
  },
}) => storeUID

export const getCurrentRepresentative = ({
  storeInfo: { currentRepresentative },
}) => currentRepresentative

export const getCurrentStore = ({ storeInfo: { currentStore } }) => currentStore

export const getToken = ({ auth: { token } }) => token

export const tryFindName = (id, items) =>
  (items.find(item => item.id === id) || {}).name || ''

export const parseUnitPriceExVat = value =>
  /(\.[0-9]{3})$/.test(value) ? parseFloat(value).toFixed(2) : value

export const padEnd = (string, targetLength, padString) => {
  const _targetLength = Math.floor(targetLength) || 0
  if (_targetLength < string.length) {
    return String(string)
  }

  const _padString = padString ? String(padString) : ' '

  let pad = ''
  const len = _targetLength - string.length
  let i = 0
  while (pad.length < len) {
    if (!_padString[i]) {
      i = 0
    }
    pad += _padString[i]
    i += 1
  }

  return String(string).slice(0) + pad
}

export const classNames = (...strings) => strings.filter(s => !!s).join(' ')

export const buildSortParams = sortItems =>
  sortItems
    .filter(item => item.length === 2)
    .filter(
      ([key, direction]) =>
        !!key && !!direction && (direction === 'asc' || direction === 'desc'),
    )
    .map(item => `sort=${item.join(',')}`)
    .join('&')

export const getSanitizedHTML = dirty =>
  DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['form', 'style', 'audio', 'video'],
  })

const toHex = str => {
  return str
    .split('')
    .map(c => c.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('')
}

const fromHex = hex => {
  return hex
    .match(/.{2}/g)
    .map(byte => String.fromCharCode(parseInt(byte, 16)))
    .join('')
}

const key = 'KBUPmuh6Jwk7W0&Ke1>D'

export function obfuscateData(data) {
  const keyHex = toHex(key)
  const dataHex = toHex(data)
  let result = ''
  for (let i = 0; i < dataHex.length; i += 2) {
    const dataByte = dataHex.substring(i, i + 2)
    const keyByte = keyHex.substring(i % keyHex.length, (i % keyHex.length) + 2)
    // eslint-disable-next-line no-bitwise
    const xorResult = (parseInt(dataByte, 16) ^ parseInt(keyByte, 16))
      .toString(16)
      .padStart(2, '0')
    result += xorResult
  }
  return result
}

export const deObfuscateData = obfuscatedData => {
  if (obfuscatedData) {
    return fromHex(obfuscateData(fromHex(obfuscatedData)))
  }
  return ''
}
